import styled from "@emotion/styled/macro";
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from "@sunrun/experience-ui-components";
import { RowRightContent } from "../../atoms/layoutAtoms";
import {
  BrandBlueButton,
  ExpandImageButtonRow,
} from "../HomeReadinessStepView";
import TeslaBattery from "../../../../assets/images/tesla-img.svg";
import FranklinBattery from "../../../../assets/images/franklin-img.svg";
import NexisBattery from "../../../../assets/images/nexis-battery.png";
import EvCharger from "../../../../assets/images/tesla-ev-charger-img.svg";
import { NonSolarDisplay } from "components/atoms/NonSolarDisplay";

type CapBatteryImageKey =
  | "TeslaBattery"
  | "SolarEdgeBattery"
  | "FranklinBattery";

const capBatteryImageKeyFromManufacturer = (
  manufacturer: string | undefined
): CapBatteryImageKey => {
  if (manufacturer === "Tesla") {
    return "TeslaBattery";
  }
  if (manufacturer === "SolarEdge Technologies") {
    return "SolarEdgeBattery";
  }
  return "FranklinBattery";
};

const CAP_BATTERY_IMAGES: Record<
  CapBatteryImageKey,
  { src: string; alt: string }
> = {
  TeslaBattery: { src: TeslaBattery, alt: "Tesla Battery" },
  SolarEdgeBattery: { src: NexisBattery, alt: "SolarEdge Battery" },
  FranklinBattery: { src: FranklinBattery, alt: "Franklin Battery" },
};

const SingleViewImage = ({
  isOnlyBattery,
  isOnlyEvCharger,
  batteryImageKey,
  showFullSizeImage,
  smallProposalImage,
  onOpenLargeImageCarousel,
  testId,
  simple = false,
}: {
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  batteryImageKey: CapBatteryImageKey | undefined;
  showFullSizeImage: boolean;
  smallProposalImage: any;
  onOpenLargeImageCarousel: () => void;
  testId?: string;
  simple?: boolean;
}) => {
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  if (isOnlyBattery) {
    const imageKey = batteryImageKey ?? "FranklinBattery";
    const batteryImage = CAP_BATTERY_IMAGES[imageKey];
    return (
      <SingleStyledNonSolarBatteryImage
        src={batteryImage.src}
        alt={batteryImage.alt}
        dataTestId={`Design-Step-Single-Battery-Display-Container-` + imageKey}
        isMobile={isMobile}
        isTablet={isTablet}
        className="Design-Step-View-Single-Battery-Display"
      />
    );
  }
  if (isOnlyEvCharger) {
    return (
      <SingleStyledNonSolarBatteryImage
        src={EvCharger}
        alt="EV Charger"
        dataTestId="Design-Step-Single-EvCharger-Display-Container"
        isMobile={isMobile}
        isTablet={isTablet}
        className="Design-Step-View-Single-EvCharger-Display"
      />
    );
  }
  return (
    <DesignImage
      data-testid={testId}
      bgImageUrl={smallProposalImage?.url}
      isMobile={isMobile}
      simple={simple}
    >
      {!showFullSizeImage && (
        <ExpandImageButtonRow>
          <RowRightContent>
            <StyledBrandBlueButton onClick={onOpenLargeImageCarousel}>
              <SvgIcon
                width={24}
                height={24}
                name={SvgNames.ArrowsOutSimple}
                color={tokens.TINTS_OFF_WHITE_90}
              />
            </StyledBrandBlueButton>
          </RowRightContent>
        </ExpandImageButtonRow>
      )}
    </DesignImage>
  );
};

const SingleStyledNonSolarBatteryImage = styled(NonSolarDisplay)<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  height: ${({ isMobile }) => (isMobile ? "200px" : "484px")};
  margin-right: ${({ isTablet }) => (isTablet ? "24px" : "64px")};
  > img {
    height: ${({ isMobile }) => (isMobile ? "168px" : "420px")};
  }
`;

const DesignImage = styled.div<{
  bgImageUrl: string;
  isMobile: boolean;
  simple?: boolean;
}>`
  ${({ simple, isMobile }) =>
    simple
      ? `
      width: calc(100% - 32px);
      margin: auto;
      margin-top: 16px;
      box-sizing: border-box;
    `
      : `
      height: ${isMobile ? "inherit" : "433px"};`}
  padding: 16px;
  aspect-ratio: 4/3;
  ${({ bgImageUrl }) =>
    bgImageUrl ? `background: url(${bgImageUrl}) no-repeat center;` : ""}
  background-size: cover;
  border-radius: 8px;
  margin-bottom: 32px;
`;

const StyledBrandBlueButton = styled(BrandBlueButton)`
  z-index: 1;
  margin-right: 24px;
`;

const ComparisonImage = ({
  isOnlyBattery,
  isOnlyEvCharger,
  batteryImageKey,
  img,
  onOpenLargeImageCarousel,
  showFullSizeImage,
  testId,
}: {
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  batteryImageKey: CapBatteryImageKey | undefined;
  img: any;
  onOpenLargeImageCarousel: () => void;
  showFullSizeImage: boolean;
  testId?: string;
}) => {
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  if (isOnlyBattery) {
    const imageKey = batteryImageKey ?? "FranklinBattery";
    const batteryImage = CAP_BATTERY_IMAGES[imageKey];
    return (
      <ComparisonStyledNonSolarBatteryImage
        src={batteryImage.src}
        alt={batteryImage.alt}
        dataTestId={
          `Design-Step-Comparison-Battery-Display-Container-` + imageKey
        }
        className="Design-Step-View-Comparison-Original-Battery-Display"
        isMobile={isMobile}
        isTablet={isTablet}
      />
    );
  } else if (isOnlyEvCharger) {
    return (
      <ComparisonStyledNonSolarBatteryImage
        src={EvCharger}
        alt="EV Charger"
        dataTestId="Design-Step-Comparison-EvCharger-Display-Container"
        className="Design-Step-View-Comparison-Original-EvCharger-Display"
        isMobile={isMobile}
        isTablet={isTablet}
      />
    );
  } else {
    return (
      <ComparisonDesignImage
        data-testid={testId}
        bgImageUrl={img?.url}
        isMobile={isMobile}
        isTablet={isTablet}
      >
        {!showFullSizeImage && (
          <ExpandImageButtonRow>
            <RowRightContent>
              <StyledBrandBlueButton onClick={onOpenLargeImageCarousel}>
                <SvgIcon
                  width={24}
                  height={24}
                  name={SvgNames.ArrowsOutSimple}
                  color={tokens.TINTS_OFF_WHITE_90}
                />
              </StyledBrandBlueButton>
            </RowRightContent>
          </ExpandImageButtonRow>
        )}
      </ComparisonDesignImage>
    );
  }
};

const ComparisonViewImage = ({
  title,
  batteryImageKey,
  img,
  onOpenLargeImageCarousel,
  showFullSizeImage,
  isOnlyBattery,
  isOnlyEvCharger,
  testId,
}: {
  title: string;
  batteryImageKey: CapBatteryImageKey | undefined;
  img: any;
  onOpenLargeImageCarousel: () => void;
  showFullSizeImage: boolean;
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  testId?: string;
}) => {
  return (
    <>
      <Typography size={18} fontWeight={"medium"}>
        {title}
      </Typography>
      <ComparisonImage
        isOnlyBattery={isOnlyBattery}
        isOnlyEvCharger={isOnlyEvCharger}
        batteryImageKey={batteryImageKey}
        img={img}
        onOpenLargeImageCarousel={onOpenLargeImageCarousel}
        showFullSizeImage={showFullSizeImage}
        testId={testId}
      />
    </>
  );
};

const ComparisonStyledNonSolarBatteryImage = styled(NonSolarDisplay)<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  height: ${({ isMobile }) => (isMobile ? "200px" : "240px")};
  margin-bottom: 22px;
  > img {
    height: ${({ isMobile }) => (isMobile ? "168px" : "200px")};
  }
`;

const ComparisonDesignImage = styled.div<{
  bgImageUrl: string;
  isMobile: boolean;
  isTablet: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: ${({ isMobile, isTablet }) =>
    isMobile ? "168px" : isTablet ? "200px" : "360px"};
  ${({ bgImageUrl }) =>
    bgImageUrl ? `background: url(${bgImageUrl}) no-repeat center;` : ""}
  background-size: cover;
  border-radius: 8px;
  margin-bottom: ${({ isMobile }) => (isMobile ? "16px" : "22px")};
`;

export {
  SingleViewImage,
  ComparisonViewImage,
  capBatteryImageKeyFromManufacturer,
  type CapBatteryImageKey,
};
